/**
 * Weekly Panels Types
 *
 * Type definitions for the Pocket Sticky Notes feature.
 * Two floating panels: Goals of the Week and Focus of the Week.
 */

/**
 * Panel type identifier
 */
export type PanelType = 'goals' | 'focus' | 'timer';

/**
 * Position coordinates for panel placement
 */
export interface Position {
  x: number;
  y: number;
}

/**
 * Size dimensions for panel
 */
export interface PanelSize {
  width: number;
  height: number;
}

/**
 * Min/max size constraints for panels
 */
export const MIN_PANEL_SIZE: PanelSize = { width: 200, height: 150 };
export const MAX_PANEL_SIZE: PanelSize = { width: 500, height: 600 };
export const DEFAULT_PANEL_SIZE: PanelSize = { width: 280, height: 200 };

/**
 * State of an individual panel
 */
export interface PanelState {
  isCollapsed: boolean;
  position: Position;
  size?: PanelSize;
}

/**
 * Complete data structure for weekly panels
 */
export interface WeeklyPanelsData {
  goalsContent: string;
  focusContent: string;
  goalsPanel: PanelState;
  focusPanel: PanelState;
}

/**
 * Icon type for SVG icons (Goals = 'target', Focus = 'flame', Timer = 'timer')
 */
export type PanelIconType = 'target' | 'flame' | 'timer';

/**
 * Color scheme for panel styling with dark mode support
 */
export interface PanelColorScheme {
  bg: string;
  border: string;
  header: string;
  headerText: string;
  icon: PanelIconType;
  iconColor: string;
}

/**
 * Panel configuration
 */
export interface PanelConfig {
  type: PanelType;
  title: string;
  placeholder: string;
  colors: PanelColorScheme;
}

/**
 * Panel color schemes for Goals and Focus panels
 * Supports light, dark, and default themes using Tailwind dark: variants
 */
export const panelColors: Record<PanelType, PanelColorScheme> = {
  goals: {
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-700',
    header: 'bg-amber-100 dark:bg-amber-900/50',
    headerText: 'text-amber-900 dark:text-amber-100',
    icon: 'target',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  focus: {
    bg: 'bg-sky-50 dark:bg-sky-950/40',
    border: 'border-sky-200 dark:border-sky-700',
    header: 'bg-sky-100 dark:bg-sky-900/50',
    headerText: 'text-sky-900 dark:text-sky-100',
    icon: 'flame',
    iconColor: 'text-sky-600 dark:text-sky-400',
  },
  timer: {
    bg: 'bg-cyan-50 dark:bg-cyan-950/40',
    border: 'border-cyan-200 dark:border-cyan-700',
    header: 'bg-cyan-100 dark:bg-cyan-900/50',
    headerText: 'text-cyan-900 dark:text-cyan-100',
    icon: 'timer',
    iconColor: 'text-cyan-600 dark:text-cyan-400',
  },
};

/**
 * Dynamics theme color schemes for Goals and Focus panels
 * Professional grey with solid background (no transparency)
 * Uses !important to override dark mode styles
 */
export const dynamicsPanelColors: Record<PanelType, PanelColorScheme> = {
  goals: {
    bg: '!bg-gray-100',
    border: '!border-gray-400',
    header: '!bg-gray-200',
    headerText: '!text-gray-800',
    icon: 'target',
    iconColor: '!text-gray-600',
  },
  focus: {
    bg: '!bg-gray-100',
    border: '!border-gray-400',
    header: '!bg-gray-200',
    headerText: '!text-gray-800',
    icon: 'flame',
    iconColor: '!text-gray-600',
  },
  timer: {
    bg: '!bg-gray-100',
    border: '!border-gray-400',
    header: '!bg-gray-200',
    headerText: '!text-gray-800',
    icon: 'timer',
    iconColor: '!text-gray-600',
  },
};

/**
 * Placeholder content for empty panels
 */
export const placeholders: Record<PanelType, string> = {
  goals: "What are your top 3 goals this week?\n\n\u2022 \n\u2022 \n\u2022 ",
  focus: "What's your main focus this week?\n\nOne thing that matters most...",
  timer: '', // Timer doesn't use placeholders
};

/**
 * Panel configurations
 */
export const panelConfigs: Record<PanelType, PanelConfig> = {
  goals: {
    type: 'goals',
    title: 'Goals of the Week',
    placeholder: placeholders.goals,
    colors: panelColors.goals,
  },
  focus: {
    type: 'focus',
    title: 'Focus of the Week',
    placeholder: placeholders.focus,
    colors: panelColors.focus,
  },
  timer: {
    type: 'timer',
    title: 'Timer',
    placeholder: placeholders.timer,
    colors: panelColors.timer,
  },
};

/**
 * Default panel states
 */
export const defaultPanelStates: Record<PanelType, PanelState> = {
  goals: {
    isCollapsed: false,
    position: { x: 20, y: 100 },
  },
  focus: {
    isCollapsed: false,
    position: { x: 320, y: 100 },
  },
  timer: {
    isCollapsed: false,
    position: { x: window.innerWidth - 220, y: 100 },
  },
};

/**
 * Default initial data
 */
export const defaultWeeklyPanelsData: WeeklyPanelsData = {
  goalsContent: '',
  focusContent: '',
  goalsPanel: defaultPanelStates.goals,
  focusPanel: defaultPanelStates.focus,
};

/**
 * PostgreSQL namespace for weekly panels settings
 */
export const WEEKLY_PANELS_NAMESPACE = 'weekly_panels';

/**
 * PostgreSQL keys for weekly panels
 */
export const WEEKLY_PANELS_KEYS = {
  GOALS_CONTENT: 'goals_content',
  FOCUS_CONTENT: 'focus_content',
  PANEL_STATE: 'panel_state',
} as const;
